import { NavLink } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import { navigation, secondaryNavigation } from '../../config/navigation';

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  return (
    <aside className={`
      fixed inset-y-0 left-0 z-20 w-64 bg-primary dark:bg-card transform 
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static
      pt-16 flex flex-col h-screen border-r border-border
    `}>
      <div className="flex items-center justify-between px-4 py-2 lg:hidden">
        <span className="text-primary-foreground dark:text-foreground font-medium">Menu</span>
        {onClose && (
          <button 
            onClick={onClose} 
            className="text-primary-foreground/80 hover:text-primary-foreground dark:text-muted-foreground dark:hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-3 py-4">
          {/* Main Navigation */}
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) => `
                  flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-primary-foreground text-primary dark:bg-primary dark:text-primary-foreground' 
                    : 'text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground dark:text-foreground dark:hover:bg-muted dark:hover:text-foreground'}
                `}
              >
                <div className="flex items-center">
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </div>
                {item.badge && (
                  <span className={`${item.badge.color} text-white text-xs px-2 py-0.5 rounded-full`}>
                    {item.badge.text}
                  </span>
                )}
              </NavLink>
            ))}
          </div>

          {/* Divider */}
          <div className="my-4 border-t border-primary-foreground/20 dark:border-border"></div>

          {/* Secondary Navigation */}
          <div className="space-y-1">
            {secondaryNavigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) => `
                  flex items-center px-3 py-2 text-sm font-medium rounded-md
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-primary-foreground text-primary dark:bg-primary dark:text-primary-foreground' 
                    : 'text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground dark:text-foreground dark:hover:bg-muted dark:hover:text-foreground'}
                `}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Help Section */}
      <div className="p-4 border-t border-primary-foreground/20 dark:border-border">
        <NavLink
          to="/aide"
          onClick={onClose}
          className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground transition-colors duration-200"
        >
          <HelpCircle className="mr-3 h-5 w-5" />
          Aide & Support
        </NavLink>
      </div>
    </aside>
  );
}
